import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../config/api';
import { PlanCalendarView } from '../components/meal-planning/PlanCalendarView';
import { ShoppingListView } from '../components/meal-planning/ShoppingListView';
import { PreferencesWizard } from '../components/meal-planning/PreferencesWizard';
import {
  CalendarDaysIcon,
  ShoppingCartIcon,
  Cog6ToothIcon,
  PlusIcon,
  SparklesIcon,
  ClockIcon,
  CheckCircleIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';

interface MealPlanningPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
  clientName?: string;
}

type TabType = 'plans' | 'shopping' | 'preferences';
type PlanStatusFilter = 'all' | 'draft' | 'active' | 'completed' | 'archived';

export const MealPlanningPage: React.FC<MealPlanningPageProps> = ({ user, coachMode, clientId, clientName }) => {
  // In coach mode, use clientId; otherwise use the logged-in user's id
  const targetUserId = coachMode && clientId ? clientId : user?.id;
  const [activeTab, setActiveTab] = useState<TabType>('plans');
  const [statusFilter, setStatusFilter] = useState<PlanStatusFilter>('all');
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreferencesWizard, setShowPreferencesWizard] = useState(false);

  const queryClient = useQueryClient();

  // Fetch all meal plans (for client if in coach mode)
  const { data: plansData, isLoading: plansLoading, error: plansError } = useQuery({
    queryKey: coachMode ? ['meal-plans', 'client', clientId, statusFilter] : ['meal-plans', statusFilter],
    queryFn: async () => {
      const result = await apiClient.getMealPlans(statusFilter === 'all' ? undefined : statusFilter, coachMode ? clientId : undefined);
      return result;
    },
    enabled: !!targetUserId,
  });

  // Fetch user preferences (for client if in coach mode)
  const { data: preferencesData } = useQuery({
    queryKey: coachMode ? ['meal-plan-preferences', 'client', clientId] : ['meal-plan-preferences'],
    queryFn: () => apiClient.getMealPlanPreferences(coachMode ? clientId : undefined),
    enabled: !!targetUserId,
  });

  // Generate meal plan mutation
  const generateMutation = useMutation({
    mutationFn: (options: any) => apiClient.generateMealPlan({ ...options, clientId: coachMode ? clientId : undefined }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plans', 'client', clientId] : ['meal-plans'] });
      if (data.plan?.id) {
        setSelectedPlanId(data.plan.id);
      }
      setIsGenerating(false);
    },
    onError: () => {
      setIsGenerating(false);
    },
  });

  // Delete meal plan mutation
  const deleteMutation = useMutation({
    mutationFn: (planId: number) => apiClient.deleteMealPlan(planId, coachMode ? clientId : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plans', 'client', clientId] : ['meal-plans'] });
      if (selectedPlanId) {
        setSelectedPlanId(null);
      }
    },
  });

  // Activate meal plan mutation
  const activateMutation = useMutation({
    mutationFn: (planId: number) => apiClient.activateMealPlan(planId, coachMode ? clientId : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plans', 'client', clientId] : ['meal-plans'] });
    },
  });

  const handleGeneratePlan = async () => {
    setIsGenerating(true);

    // Get next Monday as start date
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilMonday);

    generateMutation.mutate({
      startDate: nextMonday.toISOString().split('T')[0],
      days: 7,
      mealsPerDay: preferencesData?.preferences?.meals_per_day || 3,
      includeSnacks: preferencesData?.preferences?.include_snacks ?? true,
    });
  };

  const plans = plansData?.plans || [];
  const activePlan = plans.find((p: any) => p.status === 'active');

  const tabs = [
    { id: 'plans' as TabType, name: 'Meal Plans', icon: CalendarDaysIcon },
    { id: 'shopping' as TabType, name: 'Shopping List', icon: ShoppingCartIcon },
    { id: 'preferences' as TabType, name: 'Preferences', icon: Cog6ToothIcon },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Plans', icon: null },
    { value: 'draft', label: 'Drafts', icon: ClockIcon },
    { value: 'active', label: 'Active', icon: CheckCircleIcon },
    { value: 'completed', label: 'Completed', icon: CheckCircleIcon },
    { value: 'archived', label: 'Archived', icon: ArchiveBoxIcon },
  ];

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string }> = {
      draft: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      active: { bg: 'bg-green-100', text: 'text-green-800' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800' },
      archived: { bg: 'bg-gray-100', text: 'text-gray-800' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return `${config.bg} ${config.text}`;
  };

  if (plansLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gray-200 rounded-lg h-32 animate-pulse" />
        <div className="bg-gray-200 rounded-lg h-96 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-700 rounded-lg shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <CalendarDaysIcon className="h-8 w-8" />
                Meal Planning
                {coachMode && clientName && (
                  <span className="text-lg font-normal text-blue-200">
                    for {clientName}
                  </span>
                )}
              </h1>
              <p className="mt-2 text-blue-100">
                {coachMode
                  ? `Create and manage AI-powered meal plans for ${clientName || 'your client'}`
                  : 'AI-powered meal plans personalized to your nutrition goals and preferences'
                }
              </p>
            </div>

            <button
              onClick={handleGeneratePlan}
              disabled={isGenerating}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                isGenerating
                  ? 'bg-blue-300 cursor-not-allowed'
                  : 'bg-white text-blue-600 hover:bg-blue-50 shadow-md'
              }`}
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                  Generating...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-5 w-5" />
                  Generate New Plan
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Active Plan Quick View */}
      {activePlan && activeTab === 'plans' && !selectedPlanId && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
              <div>
                <h3 className="font-medium text-green-900">Active Plan: {activePlan.name || 'Current Week'}</h3>
                <p className="text-sm text-green-700">
                  {activePlan.start_date} to {activePlan.end_date}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedPlanId(activePlan.id)}
              className="px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
            >
              View Plan
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id !== 'plans') {
                    setSelectedPlanId(null);
                  }
                }}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="h-5 w-5" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'plans' && (
        <>
          {selectedPlanId ? (
            // Show selected plan detail
            <div>
              <button
                onClick={() => setSelectedPlanId(null)}
                className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
              >
                &larr; Back to all plans
              </button>
              <PlanCalendarView
                planId={selectedPlanId}
                onClose={() => setSelectedPlanId(null)}
                coachMode={coachMode}
                clientId={clientId}
              />
            </div>
          ) : (
            // Show plan list
            <div className="space-y-4">
              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Filter:</span>
                <div className="flex gap-1">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setStatusFilter(option.value as PlanStatusFilter)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        statusFilter === option.value
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Plans Grid */}
              {plans.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <CalendarDaysIcon className="h-12 w-12 mx-auto text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No meal plans yet</h3>
                  <p className="mt-2 text-gray-500">
                    Generate your first AI-powered meal plan to get started.
                  </p>
                  <button
                    onClick={handleGeneratePlan}
                    disabled={isGenerating}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <SparklesIcon className="h-5 w-5" />
                    Generate Your First Plan
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {plans.map((plan: any) => (
                    <div
                      key={plan.id}
                      className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setSelectedPlanId(plan.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {plan.name || `Week of ${plan.start_date}`}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {plan.start_date} to {plan.end_date}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(plan.status)}`}>
                          {plan.status}
                        </span>
                      </div>

                      {/* Nutrition summary if available */}
                      {plan.total_nutrition && (
                        <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-xs text-gray-500">Cal/day</div>
                            <div className="font-medium text-sm">
                              {Math.round(plan.total_nutrition.calories / 7)}
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-xs text-gray-500">Protein</div>
                            <div className="font-medium text-sm">
                              {Math.round(plan.total_nutrition.protein / 7)}g
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-xs text-gray-500">Carbs</div>
                            <div className="font-medium text-sm">
                              {Math.round(plan.total_nutrition.carbs / 7)}g
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-xs text-gray-500">Fat</div>
                            <div className="font-medium text-sm">
                              {Math.round(plan.total_nutrition.fat / 7)}g
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-4 flex justify-between items-center">
                        <div className="text-xs text-gray-400">
                          Created {new Date(plan.created_at).toLocaleDateString()}
                        </div>
                        <div className="flex gap-2">
                          {plan.status === 'draft' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                activateMutation.mutate(plan.id);
                              }}
                              className="text-xs text-green-600 hover:text-green-800"
                            >
                              Activate
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to delete this plan?')) {
                                deleteMutation.mutate(plan.id);
                              }
                            }}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'shopping' && (
        <ShoppingListView
          planId={activePlan?.id || selectedPlanId}
          onSelectPlan={(id) => {
            setSelectedPlanId(id);
            setActiveTab('plans');
          }}
          coachMode={coachMode}
          clientId={clientId}
        />
      )}

      {activeTab === 'preferences' && (
        <PreferencesWizard
          initialPreferences={preferencesData?.preferences}
          onSave={() => {
            queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plan-preferences', 'client', clientId] : ['meal-plan-preferences'] });
          }}
          coachMode={coachMode}
          clientId={clientId}
        />
      )}

      {/* Preferences Modal */}
      {showPreferencesWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <PreferencesWizard
              initialPreferences={preferencesData?.preferences}
              onSave={() => {
                setShowPreferencesWizard(false);
                queryClient.invalidateQueries({ queryKey: ['meal-plan-preferences'] });
              }}
              onClose={() => setShowPreferencesWizard(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
